import type {
  ImportMode,
  LayoutRowGrouping,
  LayoutRowGroupingId,
  LayoutShape,
  LayoutShapeId,
  ValueTransforms,
} from "../../../types/customCsvImport";

function rows(
  headers: string[],
  data: string[][]
): { example_headers: string[]; example_rows: string[][] } {
  return { example_headers: headers, example_rows: data };
}

/** Static fallback when capabilities omit layout_row_groupings / layout_shapes. */
export const FALLBACK_LAYOUT_ROW_GROUPINGS: LayoutRowGrouping[] = [
  {
    id: "single_row",
    label: "One row per completed trade",
    description: "Entry and exit are on the same CSV row.",
    import_mode: "closed_trade_single",
  },
  {
    id: "paired_rows",
    label: "Two rows per completed trade",
    description: "Open and close are separate rows sharing a trade ID.",
    import_mode: "closed_trade_paired",
  },
  {
    id: "per_fill",
    label: "One row per fill",
    description: "Each execution is its own row; Tessera builds trades from fills.",
    import_mode: "execution_rows",
  },
];

export const FALLBACK_LAYOUT_SHAPES: LayoutShape[] = [
  {
    id: "single_open_close_times",
    row_grouping_id: "single_row",
    import_mode: "closed_trade_single",
    label: "Open and close times on one row",
    description:
      "Each row has entry and exit on the same line. Map an exit timestamp, or a hold duration if the file has duration instead of exit datetime.",
    value_transforms: {},
    defaults: { quantity_unit: "units" },
    required_fields: [
      "symbol",
      "side",
      "entry_time",
      "entry_price",
      "entry_quantity",
      "exit_price",
      "exit_quantity",
    ],
    highlight_fields: [
      "trade_id",
      "symbol",
      "side",
      "entry_time",
      "exit_time",
      "held_duration",
      "entry_price",
      "exit_price",
      "entry_quantity",
      "exit_quantity",
      "entry_fee",
      "exit_fee",
      "gross_pnl",
    ],
    ...rows(
      [
        "Order ID",
        "Symbol",
        "Side",
        "Open Time",
        "Close Time",
        "Entry Price",
        "Close Price",
        "Quantity",
        "Commission",
        "P&L",
      ],
      [
        [
          "1001",
          "EURUSD",
          "Buy",
          "2024-01-02 10:00",
          "2024-01-02 11:00",
          "1.0850",
          "1.0860",
          "10000",
          "2.50",
          "100",
        ],
      ]
    ),
  },
  {
    id: "paired_row_kind",
    row_grouping_id: "paired_rows",
    import_mode: "closed_trade_paired",
    label: "Direction in one column",
    description:
      "Two rows per trade; one column states entry/exit and long/short (e.g. Entry Long).",
    value_transforms: { row_kind: { type: "tradingview_type" } },
    defaults: { quantity_unit: "units" },
    required_fields: ["trade_id", "row_kind", "timestamp", "price", "quantity", "symbol"],
    highlight_fields: ["trade_id", "row_kind", "timestamp", "price", "quantity", "symbol"],
    ...rows(
      ["Trade ID", "Row type", "Time", "Price", "Quantity", "P&L"],
      [
        ["42", "Entry Long", "2024-03-15 09:30", "5120.25", "2", ""],
        ["42", "Exit Long", "2024-03-15 11:45", "5138.50", "2", "36.50"],
      ]
    ),
  },
  {
    id: "paired_side_open_close",
    row_grouping_id: "paired_rows",
    import_mode: "closed_trade_paired",
    label: "Side and Open/Close columns",
    description: "Two rows per trade; separate buy/sell and open/close columns on each row.",
    value_transforms: {},
    defaults: { quantity_unit: "units" },
    required_fields: [
      "trade_id",
      "symbol",
      "side",
      "position_effect",
      "timestamp",
      "price",
      "quantity",
    ],
    highlight_fields: [
      "trade_id",
      "symbol",
      "side",
      "position_effect",
      "timestamp",
      "price",
      "quantity",
    ],
    ...rows(
      ["Trade ID", "Symbol", "Side", "Open/Close", "Time", "Price", "Quantity", "P&L"],
      [
        ["100", "EURUSD", "Buy", "Open", "2024-03-15 09:30", "1.08542", "1.00", ""],
        ["100", "EURUSD", "Sell", "Close", "2024-03-15 11:45", "1.08710", "1.00", "17.00"],
      ]
    ),
  },
  {
    id: "fill_side_open_close",
    row_grouping_id: "per_fill",
    import_mode: "execution_rows",
    label: "Side and Open/Close per fill",
    description: "Each row is one execution with explicit open/close indicator.",
    value_transforms: {
      side: { Buy: "BUY", Sell: "SELL" },
      position_effect: { Open: "OPEN", Close: "CLOSE" },
    },
    defaults: { quantity_unit: "units" },
    required_fields: [
      "execution_id",
      "timestamp",
      "symbol",
      "side",
      "quantity",
      "price",
      "position_effect",
    ],
    highlight_fields: [
      "execution_id",
      "symbol",
      "side",
      "position_effect",
      "timestamp",
      "price",
      "quantity",
    ],
    ...rows(
      ["Time", "Symbol", "Side", "Open/Close", "Price", "Quantity", "Fee", "P&L"],
      [["2024-03-15 09:30", "AAPL", "Buy", "Open", "178.45", "100", "1.00", ""]]
    ),
  },
  {
    id: "fill_direction_combined",
    row_grouping_id: "per_fill",
    import_mode: "execution_rows",
    label: "Direction in one column",
    description:
      "Each row is one fill; direction column combines side and open/close (e.g. Open Long).",
    value_transforms: {
      side: { type: "hyperliquid_dir" },
      position_effect: { type: "hyperliquid_dir" },
    },
    defaults: { quantity_unit: "units" },
    required_fields: ["execution_id", "timestamp", "symbol", "side", "quantity", "price"],
    highlight_fields: ["timestamp", "symbol", "side", "price", "quantity", "fee"],
    ...rows(
      ["Time", "Symbol", "Direction", "Price", "Quantity", "Fee", "P&L"],
      [["2024-03-15 10:05", "BTC", "Open Long", "67250", "0.15", "1.24", ""]]
    ),
  },
  {
    id: "fill_fifo",
    row_grouping_id: "per_fill",
    import_mode: "execution_rows",
    label: "No open/close column",
    description:
      "Each row is one fill; Tessera pairs them in file order. The first buy with the first sell, and so on.",
    value_transforms: {
      filter_transaction_type: "ORDER_FILL",
      transaction_type_column: "TRANSACTION TYPE",
    },
    defaults: { quantity_unit: "units" },
    required_fields: ["execution_id", "timestamp", "symbol", "side", "quantity", "price"],
    highlight_fields: ["timestamp", "symbol", "side", "price", "quantity", "fee"],
    ...rows(
      ["Time", "Symbol", "Side", "Txn type", "Price", "Quantity", "Fee", "P&L"],
      [["2024-03-15 10:00", "EURUSD", "Buy", "ORDER_FILL", "1.08542", "10000", "0.80", ""]]
    ),
  },
];

/** @deprecated Use resolveLayoutShapes from utils/layoutShapes */
export const LAYOUT_SHAPE_OPTIONS = FALLBACK_LAYOUT_SHAPES;

export type RowGroupingId = LayoutRowGroupingId;
export type { LayoutShapeId };

export function getLayoutShapesForRowGrouping(
  groupingId: LayoutRowGroupingId,
  shapes: LayoutShape[] = FALLBACK_LAYOUT_SHAPES
): LayoutShape[] {
  return shapes.filter((s) => s.row_grouping_id === groupingId);
}

export function getLayoutShape(
  id: LayoutShapeId,
  shapes: LayoutShape[] = FALLBACK_LAYOUT_SHAPES
): LayoutShape | undefined {
  return shapes.find((s) => s.id === id);
}

export function resolveLayoutShapeImportMode(
  id: LayoutShapeId,
  shapes: LayoutShape[] = FALLBACK_LAYOUT_SHAPES
): ImportMode {
  const shape = getLayoutShape(id, shapes);
  if (!shape) throw new Error(`Unknown layout shape: ${id}`);
  return shape.import_mode;
}
